class AcademyTheme {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.cartBusy = false;
    this.bindNavigation();
    this.bindGallery();
    this.bindPurchaseControls();
    this.bindProductForms();
    this.bindCart();
    this.bindCollectionSort();
    this.bindSticky();
  }

  shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  bindNavigation() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    toggle?.addEventListener('click', () => {
      const open = menu.dataset.open !== 'true';
      menu.dataset.open = String(open);
      toggle.setAttribute('aria-expanded', String(open));
    });
  }

  bindGallery() {
    document.querySelectorAll('[data-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-main-media]');
      const buttons = [...gallery.querySelectorAll('[data-thumb]')];
      buttons.forEach((button) => button.addEventListener('click', () => {
        const image = button.querySelector('img');
        if (!image || !main) return;
        main.src = button.dataset.full || image.src;
        if (button.dataset.srcset) main.srcset = button.dataset.srcset;
        if (button.dataset.sizes) main.sizes = button.dataset.sizes;
        main.alt = image.alt;
        buttons.forEach((item) => item.setAttribute('aria-pressed', 'false'));
        button.setAttribute('aria-pressed', 'true');
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }));

      const stage = gallery.querySelector('.academy-main-media');
      if (!stage || buttons.length < 2) return;
      stage.querySelector('img')?.setAttribute('draggable', 'false');
      stage.addEventListener('dragstart', (event) => event.preventDefault());
      let startX = 0;
      stage.addEventListener('pointerdown', (event) => {
        startX = event.clientX || 0;
        stage.setPointerCapture?.(event.pointerId);
      });
      stage.addEventListener('pointerup', (event) => {
        const distance = (event.clientX || startX) - startX;
        stage.releasePointerCapture?.(event.pointerId);
        if (Math.abs(distance) < 45) return;
        const current = Math.max(0, buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true'));
        const next = distance < 0 ? Math.min(current + 1, buttons.length - 1) : Math.max(current - 1, 0);
        if (next !== current) buttons[next].click();
      });
    });
  }

  bindPurchaseControls() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const input = root.querySelector('[data-product-quantity]');
      root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        if (!input) return;
        input.value = String(Math.max(1, Number(input.value || 1) - 1));
        this.syncPurchase(root);
      });
      root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        if (!input) return;
        input.value = String(Math.max(1, Number(input.value || 1) + 1));
        this.syncPurchase(root);
      });
      input?.addEventListener('change', () => this.syncPurchase(root));
      input?.addEventListener('input', () => this.syncPurchase(root));
      root.querySelectorAll('[data-bundle]').forEach((bundle) => bundle.addEventListener('change', () => this.syncPurchase(root)));
      this.syncPurchase(root);
    });
  }

  syncPurchase(root) {
    const input = root.querySelector('[data-product-quantity]');
    const setQuantity = Math.max(1, Math.floor(Number(input?.value || 1)) || 1);
    const selectedBundle = root.querySelector('[data-bundle]:checked');
    const bundleVariantId = selectedBundle?.dataset.bundleVariantId;
    const isVariantBundle = Boolean(bundleVariantId);
    const unitPrice = isVariantBundle
      ? Number(selectedBundle.dataset.bundleUnitPrice || 0)
      : Number(root.dataset.currentPrice || 0);
    const multiplier = isVariantBundle ? 1 : Math.max(1, Number(selectedBundle?.value || 1));
    const actualQuantity = setQuantity * multiplier;
    if (input) input.value = String(setQuantity);
    if (isVariantBundle) {
      const variantId = root.querySelector('[name="id"]');
      if (variantId) variantId.value = bundleVariantId;
    }
    const cartQuantity = root.querySelector('[data-cart-quantity]');
    if (cartQuantity) cartQuantity.value = String(actualQuantity);
    root.querySelectorAll('[data-bundle-price]').forEach((node) => {
      node.textContent = this.money(Number(root.dataset.currentPrice || 0) * Number(node.dataset.multiplier || 1));
    });
    const button = root.querySelector('[data-add-button]');
    if (button && !button.disabled) button.textContent = `${button.dataset.available} · ${this.money(unitPrice * actualQuantity)}`;
    document.querySelectorAll('[data-sticky-price]').forEach((node) => { node.textContent = this.money(unitPrice * actualQuantity); });
  }

  bindProductForms() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const variantsNode = root.querySelector('[data-variants-json]');
      if (!variantsNode) return;
      let variants = [];
      try { variants = JSON.parse(variantsNode.textContent); } catch { return; }
      const options = [...root.querySelectorAll('[data-option-position]')];
      const update = () => {
        const selected = options.map((group) => group.querySelector('input:checked')?.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === selected[index]));
        const id = root.querySelector('[name="id"]');
        const add = root.querySelector('[data-add-button]');
        const price = root.querySelector('[data-price]');
        const compare = root.querySelector('[data-compare]');
        if (!variant) {
          if (add) { add.disabled = true; add.textContent = add.dataset.unavailable; }
          return;
        }
        root.dataset.currentPrice = String(variant.price);
        if (id) id.value = variant.id;
        if (price) price.textContent = this.money(variant.price);
        if (compare) {
          compare.textContent = variant.compare_at_price > variant.price ? this.money(variant.compare_at_price) : '';
          compare.hidden = !compare.textContent;
        }
        if (add) {
          add.disabled = !variant.available;
          add.textContent = variant.available ? add.dataset.available : add.dataset.soldOut;
        }
        this.syncPurchase(root);
        history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      };
      options.forEach((group) => group.addEventListener('change', update));
    });
  }

  bindCart() {
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', () => this.openCart(button)));
    document.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => this.closeCart()));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeCart(); });
    document.querySelectorAll('[data-product-form]').forEach((form) => form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('[data-add-button]');
      const error = form.querySelector('[data-product-error]');
      button?.setAttribute('aria-busy', 'true');
      if (error) error.hidden = true;
      try {
        const response = await fetch(`${this.shopRoot()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) throw new Error('add failed');
        await this.refreshCart();
        this.openCart(button);
      } catch {
        if (error) error.hidden = false;
      } finally {
        button?.removeAttribute('aria-busy');
      }
    }));

    document.addEventListener('click', (event) => {
      const control = event.target.closest('[data-cart-plus],[data-cart-minus],[data-cart-remove]');
      if (!control) return;
      const key = control.dataset.cartKey;
      const item = control.closest('[data-cart-item]');
      const input = item?.querySelector('[data-cart-input]');
      if (!key) return;
      let quantity = Number(input?.value || 0);
      if (control.matches('[data-cart-plus]')) quantity += 1;
      if (control.matches('[data-cart-minus]')) quantity = Math.max(0, quantity - 1);
      if (control.matches('[data-cart-remove]')) quantity = 0;
      if (input) input.value = String(quantity);
      this.changeCartItem(key, quantity, Boolean(control.closest('[data-cart-page]')));
    });

    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-input]');
      if (!input) return;
      const quantity = Math.max(0, Math.floor(Number(input.value || 0)) || 0);
      input.value = String(quantity);
      this.changeCartItem(input.dataset.cartKey, quantity, Boolean(input.closest('[data-cart-page]')));
    });
  }

  async changeCartItem(key, quantity, refreshPage) {
    if (!key || this.cartBusy) return;
    this.cartBusy = true;
    this.drawer?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.shopRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('change failed');
      const cart = await response.json();
      if (refreshPage) {
        location.reload();
        return;
      }
      this.renderCart(cart);
      this.announce(this.drawer?.querySelector('[data-cart-live]')?.dataset.updated || '');
    } catch {
      this.announce(this.drawer?.querySelector('[data-cart-live]')?.dataset.error || '', true);
      await this.refreshCart();
    } finally {
      this.cartBusy = false;
      this.drawer?.removeAttribute('aria-busy');
    }
  }

  async refreshCart() {
    const response = await fetch(`${this.shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart failed');
    const cart = await response.json();
    this.renderCart(cart);
    return cart;
  }

  renderCart(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
    const lines = this.drawer?.querySelector('[data-cart-lines]');
    const subtotal = this.drawer?.querySelector('[data-cart-subtotal]');
    const footer = this.drawer?.querySelector('[data-cart-footer]');
    if (subtotal) subtotal.textContent = this.money(cart.total_price);
    if (footer) footer.hidden = cart.items.length === 0;
    if (!lines) return;
    if (!cart.items.length) {
      lines.innerHTML = `<div class="academy-cart-empty"><p>${this.escapeHtml(lines.dataset.empty)}</p><a href="${this.escapeHtml(lines.dataset.continueUrl)}">${this.escapeHtml(lines.dataset.continue)}</a></div>`;
      return;
    }
    lines.innerHTML = cart.items.map((item) => {
      const image = item.image ? `<a href="${this.escapeHtml(item.url)}"><img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" loading="lazy"></a>` : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${this.escapeHtml(item.variant_title)}</p>` : '';
      const key = this.escapeHtml(item.key);
      return `<article class="academy-cart-line" data-cart-item="${key}">${image}<div class="academy-cart-line__details"><a href="${this.escapeHtml(item.url)}"><strong>${this.escapeHtml(item.product_title)}</strong></a>${variant}<div class="academy-cart-line__controls"><div class="academy-quantity-stepper academy-quantity-stepper--small"><button type="button" data-cart-minus data-cart-key="${key}" aria-label="${this.escapeHtml(lines.dataset.decrease)}">−</button><input data-cart-input data-cart-key="${key}" type="number" min="0" value="${item.quantity}" inputmode="numeric" aria-label="${this.escapeHtml(lines.dataset.quantity)}"><button type="button" data-cart-plus data-cart-key="${key}" aria-label="${this.escapeHtml(lines.dataset.increase)}">+</button></div><button class="academy-cart-remove" type="button" data-cart-remove data-cart-key="${key}">${this.escapeHtml(lines.dataset.remove)}</button></div></div><strong class="academy-cart-line__price">${this.money(item.final_line_price)}</strong></article>`;
    }).join('');
  }

  openCart(trigger) {
    if (!this.drawer) return;
    this.lastTrigger = trigger;
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('academy-lock');
    this.drawer.querySelector('[data-cart-close]')?.focus();
  }

  closeCart() {
    if (!this.drawer || this.drawer.getAttribute('aria-hidden') === 'true') return;
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('academy-lock');
    this.lastTrigger?.focus();
  }

  announce(message, isError = false) {
    const live = document.querySelector('[data-cart-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => { live.textContent = message; }, 50);
    if (isError) live.setAttribute('role', 'alert'); else live.removeAttribute('role');
  }

  bindCollectionSort() {
    document.querySelector('[data-collection-sort]')?.addEventListener('change', (event) => event.target.form?.requestSubmit());
  }

  bindSticky() {
    const mainButton = document.querySelector('[data-product-root] [data-add-button]');
    const sticky = document.querySelector('[data-sticky]');
    const footer = document.querySelector('.academy-footer');
    if (!mainButton || !sticky) return;
    const observer = new IntersectionObserver((entries) => { sticky.dataset.visible = String(!entries[0].isIntersecting); });
    observer.observe(mainButton);
    if (footer) new IntersectionObserver((entries) => { if (entries[0].isIntersecting) sticky.dataset.visible = 'false'; }).observe(footer);
    sticky.querySelector('button')?.addEventListener('click', () => mainButton.click());
  }

  money(cents) {
    const format = document.documentElement.dataset.moneyFormat || '${{amount}}';
    const decimal = (Number(cents || 0) / 100).toFixed(2);
    const comma = decimal.replace('.', ',');
    const whole = String(Math.round(Number(cents || 0) / 100));
    const wholeComma = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return format
      .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, wholeComma)
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, comma)
      .replace(/{{\s*amount_no_decimals\s*}}/g, whole)
      .replace(/{{\s*amount\s*}}/g, decimal);
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
  }
}

document.addEventListener('DOMContentLoaded', () => new AcademyTheme());
